import { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronRight, Download, Share2, Trash2, RefreshCw, FileText, Map, Activity, Info, Clock, Calendar } from 'lucide-react';
import lbIcon from './components/favicon.png';

const AcquisitionIntelReports = () => {
  const [scrolled, setScrolled] = useState(false);
  const [expandedReports, setExpandedReports] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [reports, setReports] = useState([
    {
      id: 1,
      title: "Sarasota Development Area Report",
      projectName: "Project LakewoodRanch",
      location: "Sarasota, FL",
      generatedDate: "May 1, 2025",
      lastUpdated: "May 1, 2025",
      summary: "This report covers a comprehensive analysis of a 520.75-acre development area in Sarasota, FL. The analysis indicates minimal flood risks (Zone X), moderate elevation variations, and 85.30 acres of wetlands that require exclusion from development.",
      sections: [
        {
          id: 1,
          name: "Ownership",
          lastUpdated: "May 1, 2025",
          sources: ["County Property Appraiser", "Title Search Records"],
          summary: "Current ownership records not available. No encumbrances or legal disputes recorded. Further investigation recommended to verify ownership status."
        },
        {
          id: 2,
          name: "Elevation",
          lastUpdated: "April 28, 2025",
          sources: ["USGS Digital Elevation Model", "LiDAR Survey Data"],
          summary: "Elevation ranges from 12m to 28m with an average of 18.30m. Mean slope is 1.90% with minimal variation, indicating favorable development conditions with minimal earthwork requirements."
        },
        {
          id: 3,
          name: "Flood Risk",
          lastUpdated: "April 30, 2025",
          sources: ["FEMA Flood Insurance Rate Maps", "Local Floodplain Records"],
          summary: "Property lies in Zone X (minimal flood hazard) outside the Special Flood Hazard Area. Not located within 1% annual chance floodplain. FIRM Panel details available with effective date November 4, 2016."
        },
        {
          id: 4,
          name: "Soil Analysis",
          lastUpdated: "April 25, 2025",
          sources: ["USDA Soil Survey", "Geotechnical Boring Tests"],
          summary: "Dominant soil type is Loamy Sand with moderate permeability (3.777 mm/hr) and low clay content (4.45%). Moderate compaction and fertility indicators suggest favorable construction conditions."
        },
        {
          id: 5,
          name: "Wetlands Assessment",
          lastUpdated: "April 29, 2025",
          sources: ["National Wetlands Inventory", "Local Environmental Survey"],
          summary: "360.33 acres of wetlands identified, primarily Palustrine System with Freshwater Emergent and Forested/Shrub classifications. Several wetlands marked with Partially Drained/Ditched modifier requiring jurisdictional determination."
        },
      ]
    },
    {
      id: 2,
      title: "Beverly Hills Property Analysis",
      projectName: "Project Los Angeles",
      location: "Beverly Hills, CA",
      generatedDate: "April 25, 2025",
      lastUpdated: "May 2, 2025",
      summary: "Analysis of a 0.75-acre luxury residential property in Beverly Hills, CA. The property features stable soil conditions, minimal flood risk, and no wetlands. Zoning permits residential development with specific height and setback requirements.",
      sections: [
        {
          id: 1,
          name: "Ownership",
          lastUpdated: "May 2, 2025",
          sources: ["County Assessor Records", "Property Title Search"],
          summary: "Property owned by Beverly Hills Luxury Holdings LLC since 2018. No encumbrances or legal disputes recorded. Clean title with full ownership rights confirmed."
        },
        {
          id: 2,
          name: "Elevation",
          lastUpdated: "April 23, 2025",
          sources: ["High-Resolution DEM", "Topographic Survey"],
          summary: "Elevation ranges from 82m to 89m with an average of 85.5m. Relatively flat terrain with a small elevation range of 7m, facilitating easier site development."
        },
        {
          id: 3,
          name: "Flood Risk",
          lastUpdated: "April 22, 2025",
          sources: ["FEMA Flood Maps", "Historical Flooding Records"],
          summary: "Located in Flood Zone X (minimal flood hazard). Outside the 1% annual chance floodplain. FIRM Panel 06037C_212 with effective date September 25, 2008."
        },
        {
          id: 4,
          name: "Soil Analysis",
          lastUpdated: "April 20, 2025",
          sources: ["Geotechnical Report", "USDA Soil Survey"],
          summary: "Insufficient soil data available. Detailed geotechnical investigation recommended to assess soil composition, stability, and construction suitability."
        },
        {
          id: 5,
          name: "Zoning Assessment",
          lastUpdated: "April 24, 2025",
          sources: ["City Zoning Maps", "Municipal Code"],
          summary: "R-1 Single Family Residential Zone with 45ft height limit. Setbacks: 25ft front, 15ft sides, 20ft rear. Floor Area Ratio (FAR) limit of 0.45. Design review required for new construction."
        },
      ]
    },
    {
      id: 3,
      title: "Austin Development Area Report",
      projectName: "Project Austin",
      location: "Austin, TX",
      generatedDate: "April 15, 2025",
      lastUpdated: "April 30, 2025",
      summary: "Analysis of a 630.45-acre mixed-use development area in Austin, TX. The site has varied topography with some flood risk concerns in lower elevations. Environmental considerations include 110.25 acres of protected areas that must be excluded from development.",
      sections: [
        {
          id: 1,
          name: "Ownership",
          lastUpdated: "April 30, 2025",
          sources: ["Travis County Records", "Title Search Report"],
          summary: "Property owned by Austin Development Partners LP since 2020. One easement for utility access along eastern boundary. No active legal disputes affecting title."
        },
        {
          id: 2,
          name: "Elevation",
          lastUpdated: "April 14, 2025",
          sources: ["LiDAR Survey", "USGS Elevation Data"],
          summary: "Elevation ranges from 150m to 210m with significant topographical variation. Several areas with slopes exceeding 15% which may require special construction considerations."
        },
        {
          id: 3,
          name: "Flood Risk",
          lastUpdated: "April 16, 2025",
          sources: ["FEMA Maps", "Local Watershed Studies"],
          summary: "Mixed flood risk zones: 85% in Zone X (minimal risk), 15% in Zone AE (100-year floodplain). Base Flood Elevation in Zone AE ranges from 155m to 158m. Development in Zone AE subject to floodplain regulations."
        },
        {
          id: 4,
          name: "Soil Analysis",
          lastUpdated: "April 12, 2025",
          sources: ["Geotechnical Report", "USDA Soil Survey"],
          summary: "Predominantly clay loam with moderate shrink-swell potential. Average permeability of 1.2 mm/hr indicating potential drainage concerns. Some areas with shallow limestone bedrock requiring specialized foundation systems."
        },
        {
          id: 5,
          name: "Environmental Assessment",
          lastUpdated: "April 18, 2025",
          sources: ["Environmental Impact Study", "Protected Species Survey"],
          summary: "Two protected species habitats identified. 110.25 acres designated as environmentally sensitive requiring protection. Mitigation plan required for development approval including buffer zones and habitat preservation."
        },
      ]
    }
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleReport = (reportId) => {
    setExpandedReports({
      ...expandedReports,
      [reportId]: !expandedReports[reportId]
    });
  };

  const toggleSection = (reportId, sectionId) => {
    const key = `${reportId}-${sectionId}`;
    setExpandedSections({
      ...expandedSections,
      [key]: !expandedSections[key]
    });
  };

  const handleDeleteReport = (e, reportId) => {
    e.stopPropagation();
    setReportToDelete(reportId);
    setShowDeleteModal(true);
  };

  const confirmDeleteReport = () => {
    setReports(reports.filter(report => report.id !== reportToDelete));
    setShowDeleteModal(false);
    setReportToDelete(null);
  };

  const handleShareReport = (e, report) => {
    e.stopPropagation();
    // Implementation for sharing report would go here
    alert(`Sharing report: ${report.title}`);
  };

  const handleDownloadReport = (e, report) => {
    e.stopPropagation();
    // Implementation for downloading report would go here
    alert(`Downloading report: ${report.title}`);
  };

  const handleUpdateReport = (e, report) => {
    e.stopPropagation();
    // Implementation for updating report would go here
    alert(`Updating report: ${report.title}`);
  };

  const filteredReports = reports.filter(report => 
    report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      {/* Navigation Bar */}
      <div className={`bg-white shadow-md px-4 py-2 flex items-center justify-between sticky top-0 z-10 transition-all ${scrolled ? 'border-b border-gray-200' : ''}`}>
        <div className="flex items-center space-x-2">
          <img src={lbIcon} alt="LandBuilder Logo" className="w-9 h-9 object-contain" />
          <div className="font-bold text-lg text-blue-600">Acquisition Intel</div>
        </div>
        <div className="flex items-center space-x-4">
          <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600">
            <Map size={18} />
            <span>Maps</span>
          </button>
          <button className="flex items-center space-x-1 text-blue-600 border-b-2 border-blue-600">
            <FileText size={18} />
            <span>Reports</span>
          </button>
          <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600">
            <Activity size={18} />
            <span>Analytics</span>
          </button>
        </div>
      </div>
      
      {/* Header Section */}
      <div className="bg-blue-50 py-12 px-4">
        <div className="container mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Property Analysis Reports</h1>
          <p className="text-lg text-gray-600 mb-6 max-w-3xl">
            Comprehensive analysis reports for your development projects. Each report contains detailed information about property ownership, elevation, flood risk, soil conditions, and environmental factors.
          </p>
          
          <div className="flex items-center space-x-4">
            <div className="relative w-full max-w-md">
              <input
                type="text"
                placeholder="Search reports by title, project, or location..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Reports List Section */}
      <div className="container mx-auto py-12 px-4">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Your Reports ({filteredReports.length})</h2>
          <div className="flex space-x-2">
            <button className="flex items-center space-x-1 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              <span>Sort By</span>
              <ChevronDown size={16} />
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2">
              <FileText size={16} />
              <span>Generate New Report</span>
            </button>
          </div>
        </div>
        
        {filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <FileText size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">No Reports Found</h3>
            <p className="text-gray-500 mb-6">
              We couldn't find any reports matching your search criteria. Try adjusting your search or generate a new report.
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Generate New Report
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredReports.map(report => (
              <div key={report.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div 
                  className="p-4 border-b flex justify-between items-center cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleReport(report.id)}
                >
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{report.title}</h3>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-gray-500">{report.projectName}</span>
                      <span className="text-sm text-gray-500">{report.location}</span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock size={14} className="mr-1" />
                        <span>Generated: {report.generatedDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={(e) => handleUpdateReport(e, report)}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Update Report"
                    >
                      <RefreshCw size={18} />
                    </button>
                    <button 
                      onClick={(e) => handleShareReport(e, report)}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Share Report"
                    >
                      <Share2 size={18} />
                    </button>
                    <button 
                      onClick={(e) => handleDownloadReport(e, report)}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Download Report"
                    >
                      <Download size={18} />
                    </button>
                    <button 
                      onClick={(e) => handleDeleteReport(e, report.id)}
                      className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete Report"
                    >
                      <Trash2 size={18} />
                    </button>
                    {expandedReports[report.id] ? 
                      <ChevronDown size={20} className="text-gray-500 ml-2" /> : 
                      <ChevronRight size={20} className="text-gray-500 ml-2" />
                    }
                  </div>
                </div>
                
                {!expandedReports[report.id] && (
                  <div className="p-4 bg-gray-50">
                    <p className="text-gray-700">{report.summary}</p>
                    <div className="mt-3 text-sm text-gray-500 flex items-center">
                      <Calendar size={14} className="mr-1" />
                      <span>Last updated: {report.lastUpdated}</span>
                    </div>
                  </div>
                )}
                
                {expandedReports[report.id] && (
                  <div className="p-4 bg-gray-50">
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-800 mb-2">Executive Summary</h4>
                      <p className="text-gray-700">{report.summary}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-800 mb-2">Report Sections</h4>
                      <div className="space-y-3">
                        {report.sections.map(section => (
                          <div key={section.id} className="bg-white rounded-md shadow-sm overflow-hidden">
                            <div
                              className="p-3 flex justify-between items-center cursor-pointer hover:bg-blue-50"
                              onClick={() => toggleSection(report.id, section.id)}
                            >
                              <div className="font-medium text-gray-700">{section.name}</div>
                              <div className="flex items-center">
                                <span className="text-xs text-gray-500 mr-2">Updated: {section.lastUpdated}</span>
                                {expandedSections[`${report.id}-${section.id}`] ? 
                                  <ChevronDown size={16} className="text-gray-500" /> : 
                                  <ChevronRight size={16} className="text-gray-500" />
                                }
                              </div>
                            </div>
                            
                            {expandedSections[`${report.id}-${section.id}`] && (
                              <div className="p-3 border-t border-gray-100">
                                <div className="mb-3">
                                  <div className="text-sm font-medium text-gray-700 mb-1">Summary</div>
                                  <p className="text-sm text-gray-600">{section.summary}</p>
                                </div>
                                
                                <div>
                                  <div className="text-sm font-medium text-gray-700 mb-1">Data Sources</div>
                                  <ul className="list-disc pl-5 text-sm text-gray-600">
                                    {section.sources.map((source, index) => (
                                      <li key={index}>{source}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-4">
                      <button 
                        onClick={(e) => handleUpdateReport(e, report)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center space-x-1"
                      >
                        <RefreshCw size={16} />
                        <span>Update Report</span>
                      </button>
                      <button 
                        onClick={(e) => handleShareReport(e, report)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md flex items-center space-x-1"
                      >
                        <Share2 size={16} />
                        <span>Share</span>
                      </button>
                      <button 
                        onClick={(e) => handleDownloadReport(e, report)}
                        className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-md flex items-center space-x-1"
                      >
                        <Download size={16} />
                        <span>Download PDF</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <Info size={24} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete Report</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this report? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmDeleteReport}
              >
                Delete Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcquisitionIntelReports;
